import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpService } from './http.service';
import {
  TrustedDestination,
  TrustedDestinationListResponse,
  TrustedDestinationPayload,
  DomainsListResponse,
  ELinkTemplatesListResponse,
  PowerPagesListResponse,
  DEFAULT_TRUSTED_DESTINATIONS_CURRENT_PAGE,
  DEFAULT_TRUSTED_DESTINATIONS_PER_PAGE,
  TrustedDestinationBlock,
} from '../../store/entities/models/trusted-destination.model';
import { transformSnakeToCamel } from '../../store/utils/transformers';
import { ApiTableFilter } from '../../shared/models/table-filter.model';
import { TrustedDestinationCheckResult } from '../../store/entities/models/power-page.model';

export interface TrustedDestinationsListParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort?: string;
  sort_direction?: string;
  filters?: ApiTableFilter[];
  filter_link?: string;
}

export interface TrustedDestinationsExportParams {
  selected_rows: string[];
  search?: string;
  filters?: ApiTableFilter[];
  filter_link?: string;
  sort?: string;
  sort_direction?: string;
}

interface ApiBlock {
  module: string;
  applies_to_all?: boolean;
  associations?: {
    accounts?: { uuid: string; name?: string | null }[];
    domains?: { uuid: string; name?: string | null }[];
    templates?: { uuid: string; name?: string | null; applies_to_all?: boolean }[];
    template_domains?: { e_link_page_uuid?: string; template_uuid?: string; domain_uuid: string }[];
    power_pages?: { uuid: string; name?: string | null; domain_uuid?: string | null }[];
  };
  meta?: Record<string, unknown>;
}

interface ApiTrustedDestination {
  uuid: string;
  pattern_expression: string;
  regex_pattern: string;
  status: string;
  created_at: string | null;
  updated_at: string | null;
  creator_uuid: string | null;
  creator_email: string | null;
  blocks?: ApiBlock[];
  associations?: Record<string, unknown>;
}

interface ApiListResponse {
  data: ApiTrustedDestination[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class TrustedDestinationsService {
  private http = inject(HttpService);

  /**
   * Checks a destination URI against the domain's trusted-destination rules.
   * Mirrors the React `checkTrustedDestination` mutation.
   *
   * POST /destinations/{domainUuid}/check  body: { uri }
   */
  checkDestination(domainUuid: string, uri: string): Observable<TrustedDestinationCheckResult> {
    return this.http
      .post<Record<string, unknown>>(`/destinations/${domainUuid}/check`, { uri })
      .pipe(
        map((response) => {
          const data = transformSnakeToCamel(response) as {
            data?: TrustedDestinationCheckResult;
            isTrusted?: boolean;
            status?: string;
            matchedPattern?: string | null;
          };
          const result = data.data ?? data;

          return {
            isTrusted: Boolean(result.isTrusted),
            status: result.status,
            matchedPattern: result.matchedPattern ?? null,
          };
        })
      );
  }

  getList(params: TrustedDestinationsListParams = {}): Observable<TrustedDestinationListResponse> {
    const queryParams: Record<string, unknown> = {
      page: params.page ?? DEFAULT_TRUSTED_DESTINATIONS_CURRENT_PAGE,
      per_page: params.per_page ?? DEFAULT_TRUSTED_DESTINATIONS_PER_PAGE,
      sort: params.sort ?? 'created_at',
      sort_direction: params.sort_direction ?? 'desc',
      search: params.search ?? '',
    };

    if (params.filters?.length) {
      queryParams.filters = params.filters;
      queryParams.filter_link = params.filter_link ?? 'and';
    }

    return this.http
      .get<ApiListResponse>('/destinations', queryParams)
      .pipe(map((response) => this.mapListResponse(response)));
  }

  getById(uuid: string): Observable<TrustedDestination> {
    return this.http
      .get<{ data: ApiTrustedDestination }>(`/destinations/${uuid}`)
      .pipe(map((response) => this.mapTrustedDestination(response.data)));
  }

  create(payload: TrustedDestinationPayload): Observable<TrustedDestination> {
    const apiPayload = this.buildApiPayload(payload);

    return this.http
      .post<{ data: ApiTrustedDestination }>('/destinations', apiPayload)
      .pipe(map((response) => this.mapTrustedDestination(response.data)));
  }

  update(uuid: string, payload: TrustedDestinationPayload): Observable<TrustedDestination> {
    const apiPayload = this.buildApiPayload(payload);

    return this.http
      .patch<{ data: ApiTrustedDestination }>(`/destinations/${uuid}`, apiPayload)
      .pipe(map((response) => this.mapTrustedDestination(response.data)));
  }

  delete(uuid: string): Observable<void> {
    return this.http.delete<void>(`/destinations/${uuid}`);
  }

  export(params: TrustedDestinationsExportParams): Observable<void> {
    const apiParams: Record<string, unknown> = {
      selected_rows: params.selected_rows,
    };

    if (params.search) {
      apiParams.search = params.search;
    }

    if (params.filters?.length) {
      apiParams.filters = params.filters;
      apiParams.filter_link = params.filter_link ?? 'and';
    }

    if (params.sort) {
      apiParams.sort = params.sort;
      apiParams.sort_direction = params.sort_direction ?? 'asc';
    }

    return this.http.post<void>('/destinations/export', apiParams);
  }

  getDomains(search = '', page = 1, perPage = 20): Observable<DomainsListResponse> {
    const params: Record<string, unknown> = {
      page,
      per_page: perPage,
    };

    if (search) {
      params.search = search;
    }

    return this.http
      .get<Record<string, unknown>>('/destinations/domains', params)
      .pipe(map((response) => transformSnakeToCamel(response) as DomainsListResponse));
  }

  getELinkTemplates(search = '', page = 1, perPage = 20): Observable<ELinkTemplatesListResponse> {
    const params: Record<string, unknown> = {
      page,
      per_page: perPage,
    };

    if (search) {
      params.search = search;
    }

    return this.http
      .get<Record<string, unknown>>('/destinations/e-link-pages', params)
      .pipe(map((response) => transformSnakeToCamel(response) as ELinkTemplatesListResponse));
  }

  getPowerPages(search = '', page = 1, perPage = 20): Observable<PowerPagesListResponse> {
    const params: Record<string, unknown> = {
      page,
      per_page: perPage,
    };

    if (search) {
      params.search = search;
    }

    return this.http
      .get<Record<string, unknown>>('/destinations/power-pages', params)
      .pipe(map((response) => transformSnakeToCamel(response) as PowerPagesListResponse));
  }

  private mapListResponse(response: ApiListResponse): TrustedDestinationListResponse {
    return {
      data: (response.data ?? []).map((item) => this.mapTrustedDestination(item)),
      meta: {
        currentPage: response.meta.current_page,
        lastPage: response.meta.last_page,
        perPage: response.meta.per_page,
        total: response.meta.total,
      },
    };
  }

  private mapTrustedDestination(item: ApiTrustedDestination): TrustedDestination {
    const blocks = (item.blocks ?? []).map((block) => this.mapBlock(block));
    const associations = item.associations ?? {};

    return {
      uuid: item.uuid,
      patternExpression: item.pattern_expression,
      regexPattern: item.regex_pattern,
      status: item.status as 'compiled' | 'pending',
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      creatorUuid: item.creator_uuid,
      creatorEmail: item.creator_email,
      blocks,
      associations: {
        domains: this.toUuidList(associations.domains as unknown[]),
        domainsDetailed: this.mapDetailedList(associations.domains_detailed as unknown[]),
        accounts: this.toUuidList(associations.accounts as unknown[]),
        eLinkPages: this.toUuidList(associations.e_link_pages as unknown[]),
        eLinkPagesDetailed: this.mapDetailedList(associations.e_link_pages_detailed as unknown[]),
        eLinkPageDomains: this.mapELinkPageDomains(associations.e_link_page_domains as unknown[]),
        powerPages: this.toUuidList(associations.power_pages as unknown[]),
        powerPagesDetailed: this.mapPowerPagesDetailed(associations.power_pages as unknown[]),
      },
    };
  }

  private mapBlock(block: ApiBlock): TrustedDestinationBlock {
    const associations = block.associations ?? {};

    return {
      module: block.module as 'link' | 'e-link' | 'power_page',
      appliesToAll: Boolean(block.applies_to_all),
      associations: {
        accounts: (associations.accounts ?? []).map((account) => ({
          uuid: account.uuid,
          name: account.name ?? null,
        })),
        domains: (associations.domains ?? []).map((domain) => ({
          uuid: domain.uuid,
          name: domain.name ?? null,
        })),
        templates: (associations.templates ?? []).map((template) => ({
          uuid: template.uuid,
          name: template.name ?? null,
          appliesToAll: Boolean(template.applies_to_all),
        })),
        templateDomains: (associations.template_domains ?? []).map((td) => ({
          templateUuid: td.e_link_page_uuid ?? td.template_uuid ?? '',
          domainUuid: td.domain_uuid,
        })),
        powerPages: (associations.power_pages ?? []).map((pp) => ({
          uuid: pp.uuid,
          name: pp.name ?? null,
          domainUuid: pp.domain_uuid ?? null,
        })),
      },
      meta: block.meta,
    };
  }

  private toUuidList(items: unknown[] | undefined): string[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        if (item && typeof item === 'object' && 'uuid' in item) {
          return (item as { uuid: string }).uuid;
        }

        return null;
      })
      .filter((uuid): uuid is string => uuid !== null);
  }

  private mapDetailedList(items: unknown[] | undefined): { uuid: string; name: string | null }[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .filter((item): item is { uuid: string; name?: string | null } =>
        item !== null && typeof item === 'object' && 'uuid' in item
      )
      .map((item) => ({
        uuid: item.uuid,
        name: item.name ?? null,
      }));
  }

  private mapELinkPageDomains(
    items: unknown[] | undefined
  ): { eLinkPageUuid: string; domainUuid: string; domainName?: string | null; eLinkPageName?: string | null }[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .filter((item): item is Record<string, unknown> => item !== null && typeof item === 'object')
      .map((item) => ({
        eLinkPageUuid: (item.e_link_page_uuid as string) ?? (item.eLinkPageUuid as string) ?? '',
        domainUuid: (item.domain_uuid as string) ?? (item.domainUuid as string) ?? '',
        domainName: (item.domain_name as string | null) ?? (item.domainName as string | null) ?? null,
        eLinkPageName: (item.e_link_page_name as string | null) ?? (item.eLinkPageName as string | null) ?? null,
      }))
      .filter((item) => item.eLinkPageUuid && item.domainUuid);
  }

  private mapPowerPagesDetailed(
    items: unknown[] | undefined
  ): { uuid: string; name: string | null; domainUuid?: string | null; domainName?: string | null }[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .filter((item): item is Record<string, unknown> => item !== null && typeof item === 'object')
      .map((item) => {
        if (typeof item === 'string') {
          return { uuid: item, name: null, domainUuid: null, domainName: null };
        }

        return {
          uuid: (item.uuid as string) ?? '',
          name: (item.name as string | null) ?? null,
          domainUuid: (item.domain_uuid as string | null) ?? (item.domainUuid as string | null) ?? null,
          domainName: (item.domain_name as string | null) ?? (item.domainName as string | null) ?? null,
        };
      })
      .filter((item) => item.uuid);
  }

  private buildApiPayload(payload: TrustedDestinationPayload): Record<string, unknown> {
    const apiPayload: Record<string, unknown> = {
      pattern_expression: payload.patternExpression,
    };

    const blocks = payload.associations.blocks ?? [];
    const collected = {
      domains: [] as string[],
      accounts: [] as string[],
      eLinkPages: [] as string[],
      eLinkPageDomains: [] as { e_link_page_uuid: string; domain_uuid: string }[],
      powerPages: [] as string[],
    };

    for (const block of blocks) {
      if (block.module === 'link') {
        if (block.appliesToAll) {
          const accountUuid = block.associations.accounts[0]?.uuid ?? block.meta?.account_uuid;

          if (accountUuid) {
            collected.accounts.push(accountUuid as string);
          }
        } else {
          for (const domain of block.associations.domains) {
            if (domain.uuid) {
              collected.domains.push(domain.uuid);
            }
          }
        }
      }

      if (block.module === 'e-link') {
        for (const template of block.associations.templates) {
          if (template.uuid && (block.appliesToAll || template.appliesToAll)) {
            collected.eLinkPages.push(template.uuid);
          }
        }

        for (const td of block.associations.templateDomains) {
          if (td.templateUuid && td.domainUuid) {
            collected.eLinkPageDomains.push({
              e_link_page_uuid: td.templateUuid,
              domain_uuid: td.domainUuid,
            });
          }
        }
      }

      if (block.module === 'power_page') {
        for (const pp of block.associations.powerPages) {
          if (pp.uuid) {
            collected.powerPages.push(pp.uuid);
          }
        }
      }
    }

    if (collected.domains.length > 0) {
      apiPayload.domains = [...new Set(collected.domains)];
    }

    if (collected.accounts.length > 0) {
      apiPayload.accounts = [...new Set(collected.accounts)];
    }

    if (collected.eLinkPages.length > 0) {
      apiPayload.e_link_pages = [...new Set(collected.eLinkPages)];
    }

    if (collected.eLinkPageDomains.length > 0) {
      const uniqueMap = new Map<string, { e_link_page_uuid: string; domain_uuid: string }>();

      for (const item of collected.eLinkPageDomains) {
        const key = `${item.e_link_page_uuid}:${item.domain_uuid}`;

        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, item);
        }
      }
      apiPayload.e_link_page_domains = Array.from(uniqueMap.values());
    }

    if (collected.powerPages.length > 0) {
      apiPayload.power_pages = [...new Set(collected.powerPages)];
    }

    return apiPayload;
  }
}
